import analyticsInstance, { trackConversion } from "./Analytics";
import FbPixel from "@/utils/FbPixel";
import { OrderModelV2 } from "@/models/orders/OrderModelV2";
import { useConsentStore } from "@/libs/consent/consentStore";
import { ConsentCategory } from "@/libs/consent/constants";

// 주문 생성 후 이 시간이 지나면 어떤 provider에도 Purchase 이벤트를 쏘지 않음.
// 실제 first-visit은 결제 후 수 초~수 분 내에 발생하므로 30분으로도 충분한 버퍼.
// 이 window를 벗어난 방문은 "과거 주문 재열람"으로 간주 — Purchase 이벤트 아님.
// 타이트하게 잡는 이유:
// 1. Meta event_id dedup "best results within 5 minutes" — 길어질수록 dedup 품질 저하
// 2. GA4 이벤트 카운트 왜곡 방지 (transaction 자체는 dedup되지만 event 수는 부풀 수 있음)
// 3. 불필요한 provider 호출 절감
const PURCHASE_FIRE_WINDOW_MS = 30 * 60 * 1000;

// 재발화 가드 — 30분 window "안"에서의 새로고침/뒤로가기 재진입을 막는다.
// GA4는 transaction_id로 거래를 dedup하지만 Meta Pixel의 event_id dedup은
// "5분 이내 권장"이라 window 내 재로드가 이중 계수될 수 있다(#5037 거래 과대).
// localStorage 불가 환경(사파리 프라이빗 등)에서는 가드 없이 기존 동작 유지.
const PURCHASE_FIRED_KEY_PREFIX = "bh-purchase-fired:";

function hasAlreadyFired(orderId: string): boolean {
  try {
    return localStorage.getItem(PURCHASE_FIRED_KEY_PREFIX + orderId) !== null;
  } catch {
    return false;
  }
}

function markFired(orderId: string): void {
  try {
    localStorage.setItem(
      PURCHASE_FIRED_KEY_PREFIX + orderId,
      String(Date.now()),
    );
  } catch {
    // localStorage 불가 — 가드 생략
  }
}

export function trackPurchase(order: OrderModelV2): void {
  // GE 국제 주문의 Meta·Ads 는 **GE CONFIRMATION 콜백에서 이미 발화한다**
  // (#5778 · `track-ge-purchase.ts`). 여기서 또 쏘면 같은 거래가 두 번 잡힌다 — 주문 이력에서
  // 결제 후 30분 안에 이 화면을 열면 아래 window 가드를 통과하기 때문이다. **Meta 는 event_id 가
  // 갈린다**(여기는 `order.id` 32자 hex / GE 경로는 GE OrderId) → #5037 과 같은 기전의 이중 계수.
  //
  // ⚠️ GA4 는 반대다 — GE 주문의 GA4 `purchase` 는 **mgmt 서버사이드 MP** 가 쏜다(주문번호 축).
  // 즉 이 early return 이 GA4 를 막는 게 아니라, 애초에 이 화면이 GE 주문의 GA4 발화처가 아니다.
  if (order.paymentMethod === "GlobalE") return;

  const { consent, hasConsented } = useConsentStore.getState();

  // 동의 전 상태(배너만 뜬 상태)에서는 아무것도 전송하지 않음.
  // GDPR/ePrivacy·PIPA 국외이전 관점에서 opt-in 전 발화 금지가 안전 기본값.
  if (!hasConsented) return;

  if (Date.now() - order.createTime > PURCHASE_FIRE_WINDOW_MS) return;

  // 동의·window 게이트를 통과해 실제로 발화하는 시점에만 마킹한다.
  // (동의 전 방문에서 마킹하면 이후 동의 후 재방문의 정당한 첫 발화까지 막힌다)
  if (hasAlreadyFired(order.id)) return;
  markFired(order.id);

  if (consent[ConsentCategory.Performance]) {
    analyticsInstance?.purchased(order); // GA4 Purchase (분석)
  }

  if (consent[ConsentCategory.Advertising]) {
    // Google Ads 전환 — "advertising" 소관. 이전엔 Analytics.purchased에 묶여 있어서
    // performance 동의만으로도 발화되던 compliance 문제를 여기서 분리.
    // transaction_id=orderId — GA4 purchase·mgmt MP와 같은 축으로 정렬(Ads도 이 값으로 dedup).
    trackConversion(order.subtotalAmount, order.orderId, order.currency);

    FbPixel.purchased(order); // Meta Pixel (browser)

    // 실제 유저가 본 URL의 path+query를 서버로 전달해
    // Meta CAPI event_source_url을 Pixel과 일치시킴 (dedup 매칭 품질 유지).
    const pathname =
      typeof window !== "undefined"
        ? window.location.pathname + window.location.search
        : undefined;

    fetch("/api/meta/purchase/", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ order_id: order.id, pathname }),
    }).catch(() => {});
  }
}
